<script setup lang="ts">
import { computed, inject, ref } from "vue";
import { ButtonTertiaryIconXs } from "@easy/ui/buttons";
import {
    AddIcon,
    ArrowDownIcon,
    DragDropIcon,
    PencilSquareIcon,
    RecycleBinIcon,
    RocketIcon,
    VisibleIcon,
} from "@easy/ui/icons";
import { useEditorActions } from "@/views/tools/page/utils/useEditorActions";
import { useState } from "@/store/vuex-composition-mappers";
import { SectionToolInfo } from "@/apps/page/section-tool-info.interface";

const props = withDefaults(
    defineProps<{
        sectionId: number;
        isFirst?: boolean;
        isLast?: boolean;
        enabled?: boolean;
        toolInfo?: SectionToolInfo;
    }>(),
    {
        isFirst: false,
        isLast: false,
        enabled: false,
        toolInfo: () => ({
            isTool: false,
        }),
    }
);

defineOptions({
    inheritAttrs: false,
});

const displayOnlyIfPublishedForTool = computed(() => {
    if (!props.toolInfo || !props.toolInfo.isTool) return true;
    // display only if the tool is published
    return props.toolInfo.isPublished;
});
const {
    editSection,
    moveSection,
    removeSection,
    hideSection,
    addSection,
    createToolProject,
    publishToolProject,
} = useEditorActions();

// @ts-expect-error
const { preview } = useState("shared/view", ["preview"]);
const isHovered = ref(false);

const dragHandleClass = "section-drag-handle";
const isMinimap = inject("isMinimap");
</script>

<template>
    <div
        v-if="preview && !isMinimap"
        class="relative group editable-section"
        data-dnd="section"
        :data-section-id="sectionId"
        @mouseenter="isHovered = true"
        @mouseleave="isHovered = false"
        :class="{ 'pointer-events-none': !enabled }"
        v-bind="$attrs"
    >
        <button
            v-if="isHovered && !isFirst"
            class="absolute -top-[14px] rounded-md left-1/2 -translate-x-1/2 px-2 py-1 text-xs font-medium bg-blue-500 text-white uppercase flex items-center gap-1 z-[100]"
            @click.stop="addSection"
        >
            <AddIcon class="w-4 h-4" />
            {{ $t("page.sections.add_section") }}
        </button>

        <div
            v-if="isHovered"
            class="absolute right-2 top-2 flex items-center gap-1 border ct-border-1 rounded-lg px-2 py-1 shadow-md ct-bg-1 z-[200]"
        >
            <ButtonTertiaryIconXs :class="dragHandleClass" class="!cursor-move">
                <DragDropIcon />
            </ButtonTertiaryIconXs>
            <div class="border-l ct-border-1 h-4 w-px"></div>
            <ButtonTertiaryIconXs
                v-if="toolInfo.isTool && !toolInfo.isCreated"
                @click.stop="createToolProject({ tool: toolInfo.type, sectionId })"
            >
                <AddIcon />
            </ButtonTertiaryIconXs>
            <ButtonTertiaryIconXs
                v-if="toolInfo.isTool && toolInfo.isCreated && !toolInfo.isPublished"
                @click.stop="publishToolProject({ uuid: toolInfo.uuid, tool: toolInfo.type })"
            >
                <RocketIcon />
            </ButtonTertiaryIconXs>
            <ButtonTertiaryIconXs
                v-if="!toolInfo.isTool || (toolInfo.isCreated && toolInfo.isPublished)"
                @click.stop="editSection(sectionId)"
            >
                <PencilSquareIcon />
            </ButtonTertiaryIconXs>
            <ButtonTertiaryIconXs @click.stop="hideSection(sectionId)">
                <VisibleIcon />
            </ButtonTertiaryIconXs>
            <div class="border-l ct-border-1 h-4 w-px"></div>
            <ButtonTertiaryIconXs @click.stop="moveSection(sectionId, -1)" :disabled="isFirst">
                <ArrowDownIcon class="rotate-180" />
            </ButtonTertiaryIconXs>
            <ButtonTertiaryIconXs @click.stop="moveSection(sectionId, 1)" :disabled="isLast">
                <ArrowDownIcon />
            </ButtonTertiaryIconXs>
            <div class="border-l ct-border-1 h-4 w-px"></div>
            <ButtonTertiaryIconXs @click.stop="removeSection(sectionId)">
                <RecycleBinIcon class="text-red-500" />
            </ButtonTertiaryIconXs>
        </div>
        <div
            v-if="enabled && isHovered && displayOnlyIfPublishedForTool"
            :class="[
                isFirst ? 'top-0 bottom-0' : '-top-[2px] bottom-0',
                isLast ? 'bottom-0' : '-bottom-[2px]',
            ]"
            class="absolute left-0 right-0 border-2 transition-colors border-blue-500 z-50"
        ></div>
        <slot></slot>
        <button
            v-if="isHovered && !isLast"
            class="absolute left-1/2 -translate-x-1/2 -bottom-[10px] rounded-md px-2 py-1 text-xs font-medium bg-blue-500 text-white uppercase flex items-center gap-1 z-[50]"
            @click.stop="addSection"
        >
            <AddIcon class="w-4 h-4" />
            {{ $t("page.sections.add_section") }}
        </button>
    </div>
    <template v-else>
        <slot></slot>
    </template>
</template>
